import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../../../auth/useAuth";
import { ADMIN_PERMISSIONS } from "../../../../auth/adminPermissions";
import { fetchVehicleSources } from "../../../../services/vehicleSourceApi";
import { fetchVehicleLifecycleEvents } from "../../../../services/vehicleLifecycleApi";
import AdminStatePanel from "../layout/AdminStatePanel";
import AdminTableContainer from "../layout/AdminTableContainer";
import RowActionMenu from "../layout/RowActionMenu";

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function vehicleLabel(row) {
  return [row.makeNormalized, row.modelNormalized, row.year].filter(Boolean).join(" ") || "—";
}

const VehicleLifecycleEventsPage = ({ onClose, onNavigate }) => {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canView = isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_VIEW);

  const [events, setEvents] = useState([]);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const [eventType, setEventType] = useState("");
  const [vehicleSourceId, setVehicleSourceId] = useState("");
  const [previousStatus, setPreviousStatus] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [sellerType, setSellerType] = useState("");
  const [city, setCity] = useState("");
  const [eventFrom, setEventFrom] = useState("");
  const [eventTo, setEventTo] = useState("");

  const loadEvents = useCallback(async () => {
    if (!canView) return;
    try {
      setLoading(true);
      setErrorMessage("");
      const data = await fetchVehicleLifecycleEvents({
        eventType: eventType || undefined,
        vehicleSourceId: vehicleSourceId || undefined,
        previousStatus: previousStatus || undefined,
        newStatus: newStatus || undefined,
        sellerType: sellerType || undefined,
        city: city.trim() || undefined,
        eventFrom: eventFrom || undefined,
        eventTo: eventTo || undefined,
        limit: 200,
      });
      setEvents(data);
    } catch (error) {
      setEvents([]);
      setErrorMessage(error.message || "Unable to load lifecycle events.");
    } finally {
      setLoading(false);
    }
  }, [
    canView,
    eventType,
    vehicleSourceId,
    previousStatus,
    newStatus,
    sellerType,
    city,
    eventFrom,
    eventTo,
  ]);

  useEffect(() => {
    fetchVehicleSources({ sort: "source_name", sortDir: "asc" })
      .then((data) => setSources(data.sources || []))
      .catch(() => setSources([]));
  }, []);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  return (
    <div className="admin-dashboard admin-vehicle-lifecycle-events-dash admin-vehicle-sources-dash--full-width">
      <header className="admin-dashboard__header">
        <div>
          <p className="admin-dashboard__eyebrow">Mobility · Vehicle Intelligence</p>
          <h1 className="admin-dashboard__title">Vehicle Lifecycle Events</h1>
          <p className="admin-dashboard__subtitle">
            Listing availability transitions (seen, stale, removed, likely sold, reappeared).
          </p>
        </div>
        <div className="admin-dashboard__header-actions">
          <button type="button" className="admin-dashboard__action-btn" onClick={loadEvents}>
            Refresh
          </button>
          {onClose ? (
            <button type="button" className="admin-dashboard__action-btn" onClick={onClose}>
              Close
            </button>
          ) : null}
        </div>
      </header>

      <section className="admin-source-controls-dash__filters admin-vehicle-sources-dash__filters">
        <select value={eventType} onChange={(e) => setEventType(e.target.value)}>
          <option value="">All event types</option>
          <option value="LISTING_SEEN">Seen</option>
          <option value="LISTING_STALE">Stale</option>
          <option value="LISTING_REMOVED">Removed</option>
          <option value="LISTING_LIKELY_SOLD">Likely sold</option>
          <option value="LISTING_REAPPEARED">Reappeared</option>
          <option value="LISTING_STATUS_CHANGED">Status changed</option>
        </select>
        <select value={vehicleSourceId} onChange={(e) => setVehicleSourceId(e.target.value)}>
          <option value="">All sources</option>
          {sources.map((s) => (
            <option key={s.id} value={s.id}>
              {s.sourceName}
            </option>
          ))}
        </select>
        <select value={previousStatus} onChange={(e) => setPreviousStatus(e.target.value)}>
          <option value="">Previous status</option>
          <option value="ACTIVE">Active</option>
          <option value="STALE">Stale</option>
          <option value="REMOVED">Removed</option>
          <option value="LIKELY_SOLD">Likely sold</option>
        </select>
        <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
          <option value="">New status</option>
          <option value="ACTIVE">Active</option>
          <option value="STALE">Stale</option>
          <option value="REMOVED">Removed</option>
          <option value="LIKELY_SOLD">Likely sold</option>
        </select>
        <input type="text" placeholder="City" value={city} onChange={(e) => setCity(e.target.value)} />
        <select value={sellerType} onChange={(e) => setSellerType(e.target.value)}>
          <option value="">Seller type</option>
          <option value="DEALER">Dealer</option>
          <option value="MARKETPLACE">Marketplace</option>
        </select>
        <input type="date" value={eventFrom} onChange={(e) => setEventFrom(e.target.value)} />
        <input type="date" value={eventTo} onChange={(e) => setEventTo(e.target.value)} />
      </section>

      {loading ? <AdminStatePanel variant="loading" message="Loading lifecycle events…" /> : null}
      {!loading && errorMessage ? (
        <AdminStatePanel variant="error" message={errorMessage} onRetry={loadEvents} />
      ) : null}

      {!loading && !errorMessage ? (
        <AdminTableContainer>
          <table className="admin-data-table">
            <thead>
              <tr>
                <th>Event</th>
                <th>Vehicle</th>
                <th>Source</th>
                <th>Previous</th>
                <th>New</th>
                <th>Reason</th>
                <th>Missed</th>
                <th>Event at</th>
                <th>Run</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 ? (
                <tr>
                  <td colSpan={10} className="muted">
                    No lifecycle events match the current filters.
                  </td>
                </tr>
              ) : (
                events.map((row) => (
                  <tr key={row.id}>
                    <td>{row.eventType}</td>
                    <td>{vehicleLabel(row)}</td>
                    <td>{row.sourceName || row.sourceKey}</td>
                    <td>{row.previousStatus || "—"}</td>
                    <td>{row.newStatus || "—"}</td>
                    <td className="muted">{row.reason || "—"}</td>
                    <td>{row.missedRunCount ?? "—"}</td>
                    <td>{formatDate(row.eventAt)}</td>
                    <td className="muted">
                      {row.ingestionRunId ? row.ingestionRunId.slice(0, 8) : "—"}
                    </td>
                    <td>
                      <RowActionMenu
                        items={[
                          {
                            label: "Open source URL",
                            onClick: () => window.open(row.sourceUrl, "_blank", "noopener"),
                            disabled: !row.sourceUrl,
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </AdminTableContainer>
      ) : null}
    </div>
  );
};

export default VehicleLifecycleEventsPage;
